import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetEventsForTrackedGamesSimpleQuery, useGetFiltersForTrackedGamesQuery } from '../../../api/core'
import languageService from '../../../services/LanguageService'
import { BooleanMap } from '../../../types/BooleanMap'
import { MotivationKey, MotivationType } from '../../market-explorer/types/MotivationType'
import { LiveEventAppearance } from '../types/LiveEventAppearance'
import { FilterApplicability, TLiveEventsCommonFilters, LiveEventsCommonFiltersData } from '../types/LiveEventsCommonFilters'
import { getLiveEventDurations, getLiveEventPricePoints, getLiveEventCalendarAdditionalDatas } from '../utils/utils'
import { useLiveEventsSecondaryTags } from './liveEventsSecondaryTaxonomy'
import { useNonSpecialLiveEventTags } from './useLiveEventTagGroups'

type UseLiveEventsCommonFiltersParams = {
  selectedGameIds: string[]
  trackedEvents: any
  parsedParams: {
    liveEventTags: string[]
    liveEventSecondaryTags: string[]
    liveEventDurations?: string[]
    liveEventAppearances?: string[]
    liveEventPricePoints?: string[]
    additionalDatas?: string[] | 'no-selection' | undefined
    motivations: string[]
    archetypes: string[]
  }
  onFiltersChange?: (filters: TLiveEventsCommonFilters) => void
}

export const useLiveEventsCommonFilters = ({ selectedGameIds, trackedEvents, parsedParams, onFiltersChange }: UseLiveEventsCommonFiltersParams) => {
  const { data: liveEventTagsList } = useNonSpecialLiveEventTags()
  const { data: liveEventSecondaryTagsList } = useLiveEventsSecondaryTags()

  const { data: filtersForTrackedGames } = useGetFiltersForTrackedGamesQuery({ gameIds: selectedGameIds }, { skip: selectedGameIds.length === 0 })
  const { data: eventsForTrackedGamesSimple } = useGetEventsForTrackedGamesSimpleQuery({ gameIds: selectedGameIds }, { skip: selectedGameIds.length === 0 })

  const [commonFilters, setCommonFilters] = useState<TLiveEventsCommonFilters>({
    liveEventTags: [],
    liveEventSecondaryTags: [],
    additionalDatas: {},
    liveEventDurations: {},
    liveEventAppearances: {},
    liveEventPricePoints: {},
    motivations: {},
    archetypes: {},
  })

  const liveEventMotivationTypes = useMemo(() => {
    return Object.values(MotivationType)
  }, [])

  const liveEventMotivationArchetypes = useMemo(() => {
    return Object.values(MotivationKey)
  }, [])

  const liveEventDurations = useMemo(() => {
    return getLiveEventDurations()
  }, [])

  const liveEventAppearances = useMemo(() => {
    return [LiveEventAppearance.NewEventTypes, LiveEventAppearance.RecurringEventTypes, LiveEventAppearance.NonrecurringEventTypes]
  }, [])

  const liveEventPricePoints = useMemo(() => {
    return getLiveEventPricePoints()
  }, [])

  const calendarAdditionalDatas = useMemo(() => {
    return getLiveEventCalendarAdditionalDatas()
  }, [])

  // Event types (liveEventTags): from getFiltersForTrackedGames when games selected
  const selectableLiveEventTags = useMemo(() => {
    if (!filtersForTrackedGames?.types || Object.keys(filtersForTrackedGames.types).length === 0) {
      return []
    }
    return Object.keys(filtersForTrackedGames.types)
      .map((id) => ({ id, name: languageService.getTranslation('tags', id) }))
      .sort((a, b) => (a.name < b.name ? -1 : 1))
  }, [filtersForTrackedGames?.types])

  // Secondary tags: from getFiltersForTrackedGames when games selected
  const selectableLiveEventSecondaryTags = useMemo(() => {
    if (!filtersForTrackedGames?.tags || Object.keys(filtersForTrackedGames.tags).length === 0) {
      return []
    }
    return Object.keys(filtersForTrackedGames.tags)
      .map((id) => ({ id, name: languageService.getTranslation('tags', id) }))
      .sort((a, b) => (a.name < b.name ? -1 : 1))
  }, [filtersForTrackedGames?.tags])

  // Motivations: from getFiltersForTrackedGames when games selected
  const selectableLiveEventMotivations = useMemo(() => {
    if (!filtersForTrackedGames?.motivations || Object.keys(filtersForTrackedGames.motivations).length === 0) {
      return []
    }
    return Object.keys(filtersForTrackedGames.motivations) as MotivationType[]
  }, [filtersForTrackedGames?.motivations])

  // Archetypes: from getFiltersForTrackedGames when games selected
  const selectableLiveEventArchetypes = useMemo(() => {
    if (!filtersForTrackedGames?.archetypes || Object.keys(filtersForTrackedGames.archetypes).length === 0) {
      return []
    }
    return Object.keys(filtersForTrackedGames.archetypes) as MotivationKey[]
  }, [filtersForTrackedGames?.archetypes])

  // Applicability maps for the four API-driven filters (true = selectable, false = show disabled with tooltip)
  const filterApplicability = useMemo((): FilterApplicability | undefined => {
    if (!filtersForTrackedGames) {
      return undefined
    }
    return {
      liveEventTags: filtersForTrackedGames.types,
      liveEventSecondaryTags: filtersForTrackedGames.tags,
      motivations: filtersForTrackedGames.motivations as Record<MotivationType, boolean>,
      archetypes: filtersForTrackedGames.archetypes as Record<MotivationKey, boolean>,
    }
  }, [filtersForTrackedGames])

  // Get live event names array for search autocomplete (from getEventsForTrackedGamesSimple API)
  const liveEventNamesArray = useMemo(() => {
    const eventsByGame = eventsForTrackedGamesSimple?.events ?? {}
    const allEvents = Object.values(eventsByGame).flat()
    const nameSet = new Set(allEvents.map((e) => e.name))
    return Array.from(nameSet).sort((a, b) => (a < b ? -1 : 1))
  }, [eventsForTrackedGamesSimple?.events])

  // Set event tags from URL params, filtered by selectable tags for accessible games
  useEffect(() => {
    if (liveEventTagsList) {
      let selectedLiveEventTags = liveEventTagsList.filter((liveEventTag) => parsedParams.liveEventTags.includes(liveEventTag.id)) || []

      // After events are loaded, only keep tags that exist in accessible games' events
      if (!trackedEvents.isLoading && selectedGameIds.length > 0) {
        const selectableTagIds = new Set(selectableLiveEventTags.map((tag) => tag.id))
        selectedLiveEventTags = selectedLiveEventTags.filter((tag) => selectableTagIds.has(tag.id))
      }

      setCommonFilters((current) => ({ ...current, liveEventTags: selectedLiveEventTags }))
    }
  }, [parsedParams.liveEventTags, liveEventTagsList, selectableLiveEventTags, trackedEvents.isLoading, selectedGameIds.length])

  // Set event secondary tags from URL params, filtered by selectable tags for accessible games
  useEffect(() => {
    if (liveEventSecondaryTagsList) {
      let selectedLiveEventTags = liveEventSecondaryTagsList.filter((liveEventTag) => parsedParams.liveEventSecondaryTags.includes(liveEventTag.id)) || []

      // After events are loaded, only keep secondary tags that exist in accessible games' events
      if (!trackedEvents.isLoading && selectedGameIds.length > 0) {
        const selectableSecondaryTagIds = new Set(selectableLiveEventSecondaryTags.map((tag) => tag.id))
        selectedLiveEventTags = selectedLiveEventTags.filter((tag) => selectableSecondaryTagIds.has(tag.id))
      }

      setCommonFilters((current) => ({ ...current, liveEventSecondaryTags: selectedLiveEventTags }))
    }
  }, [parsedParams.liveEventSecondaryTags, liveEventSecondaryTagsList, selectableLiveEventSecondaryTags, trackedEvents.isLoading, selectedGameIds.length])

  // Set event motivation types from URL params, filtered by selectable motivations for accessible games
  useEffect(() => {
    let selectedMotivations: MotivationType[] = liveEventMotivationTypes.filter((motivationType) => parsedParams.motivations.includes(motivationType)) || []

    // After events are loaded, only keep motivations that exist in accessible games' events
    if (!trackedEvents.isLoading && selectedGameIds.length > 0) {
      const selectableMotivationsSet = new Set(selectableLiveEventMotivations)
      selectedMotivations = selectedMotivations.filter((motivationType) => selectableMotivationsSet.has(motivationType))
    }

    const selectedMotivationsMap: BooleanMap = {}

    selectedMotivations.forEach((motivationType) => {
      selectedMotivationsMap[motivationType] = true
    })

    setCommonFilters((current) => ({ ...current, motivations: selectedMotivationsMap }))
  }, [parsedParams.motivations, liveEventMotivationTypes, selectableLiveEventMotivations, trackedEvents.isLoading, selectedGameIds.length])

  // Set event motivation archetypes from URL params, filtered by selectable archetypes for accessible games
  useEffect(() => {
    let selectedArchetypes: MotivationKey[] = liveEventMotivationArchetypes.filter((motivationKey) => parsedParams.archetypes.includes(motivationKey)) || []

    // After events are loaded, only keep archetypes that exist in accessible games' events
    if (!trackedEvents.isLoading && selectedGameIds.length > 0) {
      const selectableArchetypesSet = new Set(selectableLiveEventArchetypes)
      selectedArchetypes = selectedArchetypes.filter((motivationKey) => selectableArchetypesSet.has(motivationKey))
    }

    const selectedArchetypesMap: BooleanMap = {}

    selectedArchetypes.forEach((motivationKey) => {
      selectedArchetypesMap[motivationKey] = true
    })

    setCommonFilters((current) => ({ ...current, archetypes: selectedArchetypesMap }))
  }, [parsedParams.archetypes, liveEventMotivationArchetypes, selectableLiveEventArchetypes, trackedEvents.isLoading, selectedGameIds.length])

  // Set event durations from URL params
  useEffect(() => {
    if (liveEventDurations.length > 0) {
      const durations = liveEventDurations.filter((liveEventDuration) => parsedParams?.liveEventDurations?.includes(liveEventDuration.id)) || []

      const liveEventDurationsMap: { [durationId: string]: boolean } = {}
      durations.forEach((liveEventDuration) => {
        liveEventDurationsMap[liveEventDuration.id] = true
      })

      setCommonFilters((current) => ({ ...current, liveEventDurations: liveEventDurationsMap }))
    }
  }, [parsedParams.liveEventDurations, liveEventDurations])

  // Set additional game datas from URL params
  useEffect(() => {
    const additionalDatasArray = Array.isArray(parsedParams.additionalDatas)
      ? parsedParams.additionalDatas
      : parsedParams.additionalDatas
      ? [parsedParams.additionalDatas]
      : []
    const datas = calendarAdditionalDatas.filter((data) => additionalDatasArray.includes(data.id)) || []

    const calendarAdditionalDatasMap: { [dataId: string]: boolean } = {}
    datas.forEach((data) => {
      calendarAdditionalDatasMap[data.id] = true
    })

    setCommonFilters((current) => ({ ...current, additionalDatas: calendarAdditionalDatasMap }))
  }, [parsedParams.additionalDatas, calendarAdditionalDatas])

  // Set event appearances from URL params
  useEffect(() => {
    if (liveEventAppearances.length > 0) {
      const appearances = liveEventAppearances.filter((appearance) => parsedParams?.liveEventAppearances?.includes(appearance)) || []

      const liveEventAppearancesMap: { [appearanceId: string]: boolean } = {}
      appearances.forEach((appearance) => {
        liveEventAppearancesMap[appearance] = true
      })

      setCommonFilters((current) => ({ ...current, liveEventAppearances: liveEventAppearancesMap }))
    }
  }, [parsedParams.liveEventAppearances, liveEventAppearances])

  // Set event price points from URL params
  useEffect(() => {
    if (liveEventPricePoints.length > 0) {
      const pricePoints = liveEventPricePoints.filter((pricePoint) => parsedParams?.liveEventPricePoints?.includes(pricePoint.id)) || []

      const liveEventPricePointsMap: { [pricePointId: string]: boolean } = {}
      pricePoints.forEach((pricePoint) => {
        liveEventPricePointsMap[pricePoint.id] = true
      })

      setCommonFilters((current) => ({ ...current, liveEventPricePoints: liveEventPricePointsMap }))
    }
  }, [liveEventPricePoints, parsedParams.liveEventPricePoints])

  const handleCommonFiltersChange = useCallback(
    (filters: TLiveEventsCommonFilters) => {
      setCommonFilters(filters)
      onFiltersChange?.(filters)
    },
    [onFiltersChange]
  )

  const commonFiltersData: LiveEventsCommonFiltersData = {
    liveEventTags: selectableLiveEventTags,
    liveEventSecondaryTags: selectableLiveEventSecondaryTags,
    additionalDatas: calendarAdditionalDatas,
    liveEventDurations: liveEventDurations,
    liveEventAppearances: liveEventAppearances,
    liveEventPricePoints: liveEventPricePoints,
    motivations: selectableLiveEventMotivations,
    archetypes: selectableLiveEventArchetypes,
  }

  return {
    commonFilters,
    commonFiltersData,
    filterApplicability,
    liveEventNamesArray,
    handleCommonFiltersChange,
  }
}
